import React from 'react';
import { ChevronDownIcon } from 'lucide-react';
import {
  Button,
  buttonVariants,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'shadcn-ui';
import {
  ButtonSize,
  OptionSelectionButtonLayoutProps,
  SelectableOption,
  SelectableOptionsGroup,
  isListOfSelectableOptionsGroups,
} from 'ui-builder';
import { type VariantProps } from 'class-variance-authority';

type ButtonSizeVariant = NonNullable<VariantProps<typeof buttonVariants>['size']>;

function mapSize(size?: ButtonSize): ButtonSizeVariant {
  switch (size) {
    case ButtonSize.SMALL:
      return 'sm';
    case ButtonSize.MEDIUM:
      return 'default';
    case ButtonSize.BIG:
      return 'default';
    default:
      return 'default';
  }
}

function OptionsItems(props: { options: SelectableOption<string | number>[] }) {
  return (
    <>
      {props.options.map((option) => (
        <DropdownMenuItem
          key={`option-${option.value}`}
          disabled={option.disabled}
          onClick={() => option.onSelected()}
        >
          {option.label}
        </DropdownMenuItem>
      ))}
    </>
  );
}

function OptionsGroups(props: { groups: SelectableOptionsGroup<string | number>[] }) {
  const nonEmptyGroups = props.groups.filter(
    (group) => group.options.filter((o) => !!o).length > 0,
  );

  return (
    <>
      {nonEmptyGroups.map((group, index) => (
        <React.Fragment key={`group-${index}`}>
          <DropdownMenuGroup>
            {group.title && <DropdownMenuLabel>{group.title}</DropdownMenuLabel>}
            <OptionsItems
              options={group.options.filter((o) => !!o) as SelectableOption<string | number>[]}
            />
          </DropdownMenuGroup>
          {index < nonEmptyGroups.length - 1 && <DropdownMenuSeparator />}
        </React.Fragment>
      ))}
    </>
  );
}

export default function ShadcnOptionSelectionButtonLayout(
  props: OptionSelectionButtonLayoutProps,
) {
  const size = mapSize(props.size);

  const renderTrigger = () => {
    if (props.customButton) {
      const CustomButton = props.customButton;
      return (
        <DropdownMenuTrigger asChild>
          <span>
            <CustomButton onClick={() => undefined} disabled={props.isDisabled} />
          </span>
        </DropdownMenuTrigger>
      );
    }

    return (
      <DropdownMenuTrigger asChild>
        <Button
          variant="default"
          size={size}
          disabled={props.isDisabled}
          data-testid={props.testId}
          title={props.title}
        >
          {props.text}
          <ChevronDownIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
    );
  };

  return (
    <DropdownMenu>
      {renderTrigger()}
      <DropdownMenuContent align="end">
        {isListOfSelectableOptionsGroups(props.options) ? (
          <OptionsGroups groups={props.options as SelectableOptionsGroup<string | number>[]} />
        ) : (
          <OptionsItems options={props.options as SelectableOption<string | number>[]} />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
